import { useDefinition } from "../../../hooks/useDefinition";
import {
	getRotatedCorners,
	ILine,
	CornerId,
	TransformType,
	zoneTransformToSnappingRect,
} from "../../guideline-overlay";
import { ZoneSelectionBoxHandlers } from "../../zone-selection-box/zone-selection-box-handlers";

// Re-export TransformType so existing consumers (e.g. use-zone-rotate) can keep importing from here
export { TransformType } from "../../guideline-overlay";

// Import Vector2 and LineSegment2 from shared snap utilities
type Vector2 = eXpress.creator.snap.Vector2;
type LineSegment2 = eXpress.creator.snap.LineSegment2;
const Vector2 = eXpress.creator.snap.Vector2;
const LineSegment2 = eXpress.creator.snap.LineSegment2;

// Snapping validation constants
const MAX_SNAP_DISTANCE_RATIO = 0.1; // Maximum snap distance as ratio of zone diagonal (10%)
const MAX_SCALE_FACTOR = 2.0; // Maximum size increase during snapping (2x)
const MIN_SCALE_FACTOR = 0.5; // Minimum size decrease during snapping (0.5x)

interface ICornerAndLines {
	cornerId: CornerId;
	linesToSnap: IDistanceAndLine[];
}
interface IDistanceAndLine {
	line: LineSegment2;
	distance: number;
}

/**
 * Reconstruct a rectangle from corner points.
 * Uses distances to calculate width/height (works for rotated rectangles).
 *
 * @param corners - Array of 4 corner points: [TopLeft, TopRight, BottomRight, BottomLeft]
 * @returns A Rect with left, top, width, height calculated from corners
 */
function getRectFromCorners(corners: readonly eXpress.math.Point[]): eXpress.math.Rect {
	const topLeft = corners[CornerId.TopLeft];
	const topRight = corners[CornerId.TopRight];
	const bottomLeft = corners[CornerId.BottomLeft];

	// Width is the distance from TopLeft to TopRight (works for any rotation)
	const width = topLeft.distanceTo(topRight);

	// Height is the distance from TopLeft to BottomLeft (works for any rotation)
	const height = topLeft.distanceTo(bottomLeft);

	return eXpress.math.rect(topLeft.left, topLeft.top, width, height);
}

function getLinesInSnappingRange(
	corner: eXpress.math.Point,
	lines: LineSegment2[],
): IDistanceAndLine[] {
	const linesInSnappingRange: IDistanceAndLine[] = [];
	lines.forEach((line) => {
		const closestPoint = line.closestPoint({ x: corner.left, y: corner.top }, true);
		const distance = corner.distanceTo({
			left: closestPoint.x,
			top: closestPoint.y,
		});
		if (distance <= line.maxDist) {
			linesInSnappingRange.push({
				line,
				distance,
			} as IDistanceAndLine);
		}
	});

	linesInSnappingRange.sort((prev, curr) => {
		if (prev.distance < curr.distance) return 1;
		return -1;
	});

	return linesInSnappingRange;
}

// get all corners adjacent to the current handle
function getCornersFromHandle(handle: ZoneSelectionBoxHandlers): CornerId[] {
	switch (handle) {
		// sideHandles
		case ZoneSelectionBoxHandlers.Top:
			return [CornerId.TopLeft, CornerId.TopRight];
		case ZoneSelectionBoxHandlers.Right:
			return [CornerId.TopRight, CornerId.BottomRight];
		case ZoneSelectionBoxHandlers.Bottom:
			return [CornerId.BottomRight, CornerId.BottomLeft];
		case ZoneSelectionBoxHandlers.Left:
			return [CornerId.BottomLeft, CornerId.TopLeft];
		// cornerHandles
		case ZoneSelectionBoxHandlers.TopLeft:
			return [CornerId.BottomLeft, CornerId.TopLeft, CornerId.TopRight];
		case ZoneSelectionBoxHandlers.TopRight:
			return [CornerId.TopLeft, CornerId.TopRight, CornerId.BottomRight];
		case ZoneSelectionBoxHandlers.BottomLeft:
			return [CornerId.TopLeft, CornerId.BottomLeft, CornerId.BottomRight];
		case ZoneSelectionBoxHandlers.BottomRight:
			return [CornerId.TopRight, CornerId.BottomRight, CornerId.BottomLeft];
		case ZoneSelectionBoxHandlers.None:
		case ZoneSelectionBoxHandlers.All:
		case ZoneSelectionBoxHandlers.Sides:
		case ZoneSelectionBoxHandlers.Corners:
		default:
			return [CornerId.TopLeft, CornerId.TopRight, CornerId.BottomRight, CornerId.BottomLeft];
	}
}

/**
 * Get the edge normal direction for a side handle. \
 * This returns the direction perpendicular to the edge being moved. \
 * For rotated zones, we calculate this from the actual corner positions.
 */
function getEdgeNormal(
	corners: readonly eXpress.math.Point[],
	handle: ZoneSelectionBoxHandlers,
): eXpress.math.Vector {
	let edgeStart: eXpress.math.Point;
	let edgeEnd: eXpress.math.Point;

	// Get the two corners that form the edge being moved
	switch (handle) {
		case ZoneSelectionBoxHandlers.Top:
			edgeStart = corners[CornerId.TopLeft];
			edgeEnd = corners[CornerId.TopRight];
			break;
		case ZoneSelectionBoxHandlers.Bottom:
			edgeStart = corners[CornerId.BottomLeft];
			edgeEnd = corners[CornerId.BottomRight];
			break;
		case ZoneSelectionBoxHandlers.Left:
			edgeStart = corners[CornerId.TopLeft];
			edgeEnd = corners[CornerId.BottomLeft];
			break;
		case ZoneSelectionBoxHandlers.Right:
			edgeStart = corners[CornerId.TopRight];
			edgeEnd = corners[CornerId.BottomRight];
			break;
		case ZoneSelectionBoxHandlers.None:
		case ZoneSelectionBoxHandlers.TopLeft:
		case ZoneSelectionBoxHandlers.TopRight:
		case ZoneSelectionBoxHandlers.BottomLeft:
		case ZoneSelectionBoxHandlers.BottomRight:
		case ZoneSelectionBoxHandlers.All:
		case ZoneSelectionBoxHandlers.Corners:
		case ZoneSelectionBoxHandlers.Sides:
		default:
			return eXpress.math.vector(0, 0);
	}

	// Calculate edge vector
	const edgeX = edgeEnd.left - edgeStart.left;
	const edgeY = edgeEnd.top - edgeStart.top;

	// Normal is perpendicular to the edge (pointing outward from the rect center)
	// For a counter-clockwise rotation: normal = (-edgeY, edgeX)
	// We'll normalize it
	const length = Math.sqrt(edgeX * edgeX + edgeY * edgeY);
	if (length === 0) return eXpress.math.vector(0, 0);

	// The direction depends on which side - we want outward normal
	switch (handle) {
		case ZoneSelectionBoxHandlers.Top:
			return eXpress.math.vector(edgeY / length, -edgeX / length);
		case ZoneSelectionBoxHandlers.Bottom:
			return eXpress.math.vector(-edgeY / length, edgeX / length);
		case ZoneSelectionBoxHandlers.Left:
			return eXpress.math.vector(-edgeY / length, edgeX / length);
		case ZoneSelectionBoxHandlers.Right:
			return eXpress.math.vector(edgeY / length, -edgeX / length);
		default:
			return eXpress.math.vector(0, 0);
	}
}

/**
 * Project a displacement vector onto the edge normal direction.
 * This ensures we only move the edge in the perpendicular direction.
 */
function projectOntoNormal(
	diff: eXpress.math.Vector,
	normal: eXpress.math.Vector,
): eXpress.math.Vector {
	// Project diff onto normal direction
	const dotProduct = diff.dot(normal);
	return normal.scale(dotProduct);
}

function updateZoneResizeTransform(
	lines: LineSegment2[],
	position: eXpress.math.IRectWithAngle,
	handle: ZoneSelectionBoxHandlers | undefined,
	zone: eXpress.core.ITransformableZone | undefined,
): eXpress.math.IRectWithAngle {
	const newRotatedRect = {
		left: Math.round(position.left),
		top: Math.round(position.top),
		width: Math.round(position.width),
		height: Math.round(position.height),
		angle: position.angle,
	} satisfies eXpress.math.IRectWithAngle;

	if (!zone || !handle) {
		return newRotatedRect;
	}

	const allCorners = getRotatedCorners(newRotatedRect);

	const corners = getCornersFromHandle(handle);

	// list of corners and all lines the corresponding corner can snap to
	const cornersAndSnaps: ICornerAndLines[] = [];
	corners.forEach((cornerId) => {
		// get list of lines for corner where line.maxdist < distance, sorted from closest to furthest
		const linesToSnap = getLinesInSnappingRange(allCorners[cornerId], lines);
		cornersAndSnaps.push({
			cornerId,
			linesToSnap,
		});
	});

	const transformedCorners = getRotatedCorners(newRotatedRect);
	// filter to get corners with their closest line
	const cornerLines = cornersAndSnaps.map((cs) => ({
		cornerId: cs.cornerId,
		snapLine: cs.linesToSnap.filter((lts) => lts.distance).last(),
	}));

	// get closest distance from any corner to relevant snapping line
	const closestSnap = cornerLines.filter((sd) => sd.snapLine && sd.snapLine.distance).first()
		?.snapLine?.distance;

	// only check for corners that are aligned to the closest line
	const cornersOnSnap = cornerLines.sort((cl) => {
		if (cl.snapLine && closestSnap && cl.snapLine.distance <= closestSnap) return -1;
		return 1;
	});
	let transformedRect = {
		...getRectFromCorners(allCorners),
		angle: position.angle,
	};

	// there should always be 2 corners that have the same line to snap to
	if (cornersOnSnap.length < 2) return position;

	// eslint-disable-next-line no-bitwise
	if (ZoneSelectionBoxHandlers.Sides & handle) {
		let hasValidSnap = false;
		const originalRect = { ...transformedRect };

		// Get the edge normal for this side handle (works correctly for rotated zones)
		const edgeNormal = getEdgeNormal(allCorners, handle);
		if (edgeNormal.width === 0 && edgeNormal.height === 0) return position;

		cornersOnSnap.forEach((cornerSnap) => {
			const { cornerId, snapLine: linesToSnap } = cornerSnap;
			if (!linesToSnap) return;
			const closestPoint = linesToSnap.line.closestPoint(
				{
					x: transformedCorners[cornerId].left,
					y: transformedCorners[cornerId].top,
				},
				true,
			);

			// Check if the snapping distance is reasonable
			const closestPointOnLine = eXpress.math.point({
				left: closestPoint.x,
				top: closestPoint.y,
			});
			const snapDistance = closestPointOnLine.distanceTo(transformedCorners[cornerId]);

			// Only snap if the distance is reasonable (less than 10% of the zone size)
			const zoneDiagonalSize = eXpress.math.vector(position).length();
			if (snapDistance > zoneDiagonalSize * MAX_SNAP_DISTANCE_RATIO) return;

			const diff = closestPointOnLine.from(transformedCorners[cornerId]);

			// Project the displacement onto the edge normal direction
			// This ensures we only move in the perpendicular direction (works for rotated zones)
			const projectedDiff = projectOntoNormal(diff, edgeNormal);

			// Move the corner by the projected displacement
			transformedCorners[cornerId] = transformedCorners[cornerId].addVector(projectedDiff);

			// Check that the new zone size doesn't become excessively large or small
			const testRect = getRectFromCorners(transformedCorners);
			const scaleFactor =
				testRect.toVector().length() / eXpress.math.vector(originalRect).length();

			// Don't allow more than 2x size increase or less than 0.5x size decrease during snapping
			if (scaleFactor > MAX_SCALE_FACTOR || scaleFactor < MIN_SCALE_FACTOR) return;

			transformedRect = {
				...testRect,
				angle: position.angle,
			};
			hasValidSnap = true;
		});

		// Only return the transformed rect if we had at least one valid snap
		if (!hasValidSnap) return position;
		// eslint-disable-next-line no-bitwise
	} else if (ZoneSelectionBoxHandlers.Corners & handle) {
		// For corner handles, we use the same approach as desktop:
		// Find the intersection between the line from pivot to dragged corner and the snap lines.
		// This automatically maintains aspect ratio because we scale along the diagonal.
		let draggedCornerId: number;
		let pivotCornerId: number;

		switch (handle) {
			case ZoneSelectionBoxHandlers.TopLeft:
				draggedCornerId = CornerId.TopLeft;
				pivotCornerId = CornerId.BottomRight;
				break;
			case ZoneSelectionBoxHandlers.TopRight:
				draggedCornerId = CornerId.TopRight;
				pivotCornerId = CornerId.BottomLeft;
				break;
			case ZoneSelectionBoxHandlers.BottomLeft:
				draggedCornerId = CornerId.BottomLeft;
				pivotCornerId = CornerId.TopRight;
				break;
			case ZoneSelectionBoxHandlers.BottomRight:
				draggedCornerId = CornerId.BottomRight;
				pivotCornerId = CornerId.TopLeft;
				break;
			case ZoneSelectionBoxHandlers.Top:
			case ZoneSelectionBoxHandlers.Left:
			case ZoneSelectionBoxHandlers.Right:
			case ZoneSelectionBoxHandlers.Bottom:
			case ZoneSelectionBoxHandlers.All:
			case ZoneSelectionBoxHandlers.Corners:
			case ZoneSelectionBoxHandlers.Sides:
			default:
				return position;
		}

		// Create a line from pivot corner through the dragged corner (the diagonal)
		const pivotCorner = allCorners[pivotCornerId];
		const draggedCorner = allCorners[draggedCornerId];

		const pivotVec = Vector2.create(pivotCorner.left, pivotCorner.top);
		const draggedVec = Vector2.create(draggedCorner.left, draggedCorner.top);
		const diagonalLine = LineSegment2.create(pivotVec, draggedVec);

		// Find the closest intersection point between the diagonal and all snap lines
		// This is the same approach as desktop's getClosestIntersection
		let closestIntersection: Vector2 | null = null;
		let closestDistSq = Number.POSITIVE_INFINITY;

		for (const line of lines) {
			// Check if the lines are not parallel (use minDot check like desktop)
			const diagDiff = diagonalLine.getVectorDiff();
			const lineDiff = line.getVectorDiff();
			let dot = diagDiff.dot(lineDiff);
			if (dot < 0) dot = -dot;
			diagDiff.free();
			lineDiff.free();

			// If dot < minDot, lines are not parallel enough, so we can find intersection
			if (dot < line.minDot) {
				// Find intersection point (full=true to treat as infinite lines)
				const intersection = diagonalLine.intersect(line, true);
				if (intersection !== null && intersection !== false) {
					// Calculate distance from current dragged corner to intersection
					const distSq = draggedVec.distanceSq(intersection);

					// Check if this is within snap range (use line's maxDist)
					if (distSq < line.maxDist * line.maxDist && distSq < closestDistSq) {
						if (closestIntersection) {
							closestIntersection.free();
						}
						closestIntersection = intersection;
						closestDistSq = distSq;
					} else {
						intersection.free();
					}
				}
			}
		}

		// Clean up the diagonal line
		diagonalLine.free();

		// If no intersection found, no snapping
		if (!closestIntersection) {
			return position;
		}

		// The intersection point is the new position for the dragged corner
		// Calculate scale factor based on distance from pivot
		const originalDistance = draggedCorner.distanceTo(pivotCorner);
		if (originalDistance === 0) {
			closestIntersection.free();
			return position;
		}

		const newDistance = Math.sqrt(
			Math.pow(closestIntersection.x - pivotCorner.left, 2) +
				Math.pow(closestIntersection.y - pivotCorner.top, 2),
		);
		const scaleFactor = newDistance / originalDistance;

		// Check that the new zone size doesn't become excessively large or small
		if (scaleFactor > MAX_SCALE_FACTOR || scaleFactor < MIN_SCALE_FACTOR) {
			closestIntersection.free();
			return position;
		}

		// Calculate the new rectangle by scaling all corners around the pivot
		const testCorners = allCorners.map((corner) => {
			const dx = corner.left - pivotCorner.left;
			const dy = corner.top - pivotCorner.top;
			return new eXpress.math.Point(
				pivotCorner.left + dx * scaleFactor,
				pivotCorner.top + dy * scaleFactor,
			);
		});

		closestIntersection.free();

		transformedRect = {
			...getRectFromCorners(testCorners),
			angle: position.angle,
		};
	}

	return transformedRect;
}
function updateZoneMoveTransform(
	lines: LineSegment2[],
	position: eXpress.math.IRectWithAngle,
): eXpress.math.IRectWithAngle {
	const newRect = {
		left: Math.round(position.left),
		top: Math.round(position.top),
		width: Math.round(position.width),
		height: Math.round(position.height),
		angle: position.angle,
	};

	const corners = getRotatedCorners(newRect);

	// list of corners and all lines the corresponding corner should snap to
	const cornersAndSnaps: ICornerAndLines[] = [];
	corners.forEach((corner, index) => {
		// get list of lines for corner where line.maxdist < distance, sorted from closest to furthest
		const linesToSnap = getLinesInSnappingRange(corner, lines);
		cornersAndSnaps.push({
			cornerId: index,
			linesToSnap,
		});
	});

	const transformedCorners = corners;
	cornersAndSnaps.forEach((cornerSnap) => {
		const { cornerId, linesToSnap } = cornerSnap;

		// snap corner to corresponding lines in snapping distance
		linesToSnap.forEach((lineToSnap) => {
			const closestPoint = lineToSnap.line.closestPoint(
				{
					x: transformedCorners[cornerId].left,
					y: transformedCorners[cornerId].top,
				},
				true,
			);
			const closePoint = eXpress.math.point({
				left: closestPoint.x,
				top: closestPoint.y,
			});
			const diff = closePoint.sub(
				transformedCorners[cornerId].left,
				transformedCorners[cornerId].top,
			);

			// Move every corner with the distance between current corner and closest point on line,
			// lines are sorted on distance big > small, so the last line we get is the closest one.
			for (let i = 0; i <= 3; i += 1) {
				transformedCorners[i] = transformedCorners[i].addPoint(diff);
			}
		});
	});

	const transformedRect = getRectFromCorners(transformedCorners);
	return {
		...transformedRect,
		angle: position.angle,
	};
}

/**
 * Snap a rotated zone to the closest angularly-aligned snap line.
 *
 * Algorithm (mirrors desktop's snapToRotate + rotateCorners):
 * 1. Check the top edge (TL→TR) and left edge (TL→BL) against all snap lines.
 * 2. For each edge, compute the angular alignment (dot product) with each snap line.
 *    The dot product measures how parallel the edge is to the line (1.0 = perfect).
 * 3. Track the best-aligned line across both edges (highest dot, then closest distance as tiebreaker).
 * 4. Compute the angle delta between the zone edge and the best snap line.
 * 5. Apply the delta to the zone's angle, then reconstruct the top-left position from the center
 *    (center is invariant during rotation) and the new angle.
 *
 * @param lines - The snap lines to test against
 * @param position - The current zone rectangle (top-left origin, with angle)
 * @returns The adjusted rectangle with snapped angle
 */
function updateZoneRotateTransform(
	lines: LineSegment2[],
	position: eXpress.math.IRectWithAngle,
): eXpress.math.IRectWithAngle {
	const corners = getRotatedCorners(position);
	const edgePairs: ReadonlyArray<[CornerId, CornerId]> = [
		[CornerId.TopLeft, CornerId.TopRight],
		[CornerId.TopLeft, CornerId.BottomLeft],
	];

	let bestDot = 0;
	let bestDistanceSq = Number.POSITIVE_INFINITY;
	let bestDeltaAngle = 0;

	for (const [startCornerId, endCornerId] of edgePairs) {
		const edgeStart = corners[startCornerId];
		const edgeEnd = corners[endCornerId];
		const edgeDirection = Vector2.create(
			edgeEnd.left - edgeStart.left,
			edgeEnd.top - edgeStart.top,
		).normalize();
		const edgeAngle = Math.atan2(edgeDirection.y, edgeDirection.x);
		const edgeStartVector = Vector2.create(edgeStart.left, edgeStart.top);

		for (const line of lines) {
			const lineDirection = line.getVectorDiff();
			// Absolute dot product: direction doesn't matter, only alignment.
			// Flip lineDirection if anti-parallel so the delta angle is minimal.
			let dot = edgeDirection.dot(lineDirection);

			if (dot < 0) {
				dot = -dot;
				lineDirection.multiplyScalar(-1);
			}

			// Skip lines below the angular alignment threshold (minDot ≈ cos(4°) ≈ 0.998)
			if (dot <= line.minDot) {
				lineDirection.free();
				continue;
			}

			// Delta angle: how much to rotate the zone edge to align with this snap line
			const lineAngle = Math.atan2(lineDirection.y, lineDirection.x);
			// Normalize to [-pi, pi] so we always apply the smallest rotation delta.
			// This avoids edge cases around the -180°/180° boundary.
			const deltaAngle = Math.atan2(
				Math.sin(lineAngle - edgeAngle),
				Math.cos(lineAngle - edgeAngle),
			);

			// Use closest distance as tiebreaker between equally-aligned lines
			const closestPoint = line.closestPoint(edgeStartVector, true);
			const distanceSq = edgeStartVector.distanceSq(closestPoint);

			if (dot > bestDot || (dot === bestDot && distanceSq < bestDistanceSq)) {
				bestDot = dot;
				bestDistanceSq = distanceSq;
				bestDeltaAngle = deltaAngle;
			}

			closestPoint.free();
			lineDirection.free();
		}

		edgeDirection.free();
		edgeStartVector.free();
	}

	// No snap line was aligned enough
	if (bestDot === 0) {
		return position;
	}

	// Apply the angle delta and normalize to [0, 360)
	const newAngle = eXpress.math.normalizeAnglePoint(
		position.angle + eXpress.math.radiansToDegrees(bestDeltaAngle),
	);

	// The center of a rectangle is invariant under rotation (midpoint of any diagonal).
	// Compute new top-left from center, half-dimensions, and the new angle.
	const centerX = (corners[CornerId.TopLeft].left + corners[CornerId.BottomRight].left) / 2;
	const centerY = (corners[CornerId.TopLeft].top + corners[CornerId.BottomRight].top) / 2;

	// Top-left = center + rotation(-halfWidth, -halfHeight)
	const angleRad = (newAngle * Math.PI) / 180;
	const halfWidth = position.width / 2;
	const halfHeight = position.height / 2;

	const offsetX = halfWidth * Math.cos(angleRad) - halfHeight * Math.sin(angleRad);
	const offsetY = halfWidth * Math.sin(angleRad) + halfHeight * Math.cos(angleRad);

	return {
		left: centerX - offsetX,
		top: centerY - offsetY,
		width: position.width,
		height: position.height,
		angle: newAngle,
	};
}

/**
 * Update zoneTransform based on snappingRect
 * Should we add argument to round?
 */
function snappingRectToZoneTransform(
	snappingRect: eXpress.math.IRectWithAngle,
	zoneTransform: eXpress.core.IZoneTransform,
) {
	const { borderThickness } = zoneTransform;

	zoneTransform.left = snappingRect.left + borderThickness;
	zoneTransform.top = snappingRect.top + borderThickness;
	zoneTransform.width = snappingRect.width - borderThickness * 2;
	zoneTransform.height = snappingRect.height - borderThickness * 2;
	zoneTransform.angle = snappingRect.angle;
}

/**
 * returns the new zoneTransform after snapping
 */
function snapTransform(
	zoneTransform: eXpress.core.ZoneTransform,
	lines: readonly ILine[],
	transformType: TransformType,
	handle?: ZoneSelectionBoxHandlers,
	zone?: eXpress.core.ITransformableZone,
): eXpress.core.ZoneTransform {
	const lineSegments = lines.map((line) => line.lineSegment);

	const position = zoneTransformToSnappingRect(zoneTransform);
	let newPosition: eXpress.math.IRectWithAngle;
	if (transformType === TransformType.Move) {
		newPosition = updateZoneMoveTransform(lineSegments, position);
	} else if (transformType === TransformType.Resize) {
		newPosition = updateZoneResizeTransform(lineSegments, position, handle, zone);
	} else {
		newPosition = updateZoneRotateTransform(lineSegments, position);
	}

	const newZoneTransform = zoneTransform.clone();

	if (newPosition) {
		// convert back
		snappingRectToZoneTransform(newPosition, newZoneTransform);
	}

	return newZoneTransform;
}

export function useSnapping() {
	const definition = useDefinition();

	if (!definition) return undefined;

	return (
		newTransform: eXpress.core.ZoneTransform,
		lines: readonly ILine[],
		transformType: TransformType,
		handle?: ZoneSelectionBoxHandlers,
		zone?: eXpress.core.ITransformableZone,
	) => snapTransform(newTransform, lines, transformType, handle, zone);
}
